import { useState, useRef, useCallback, useEffect } from 'react'
import { ItemPickerDropdown } from './item-picker-dropdown'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

const StripingIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M4 20h16M4 20V4M8 16V8M12 16V6M16 16V10M20 16V4" />
  </svg>
)

const fmt = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

interface StripingSubSectionProps {
  title: string
  stripingCategory: string
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

function calcStripingTotal(qty: number, rate: number, newLayout: boolean, doubleCoat: boolean, newLayoutAdd: number, doubleCoatAdd: number): number {
  let total = qty * rate
  if (newLayout && newLayoutAdd) total += qty * newLayoutAdd
  if (doubleCoat && doubleCoatAdd) total += qty * doubleCoatAdd
  return total
}

/**
 * Resolve a striping line's surcharge amounts. Prefer the amounts snapshotted on the
 * line when it was added, so a later catalog edit never changes this line's price;
 * fall back to the live pricing item only for legacy lines that never stored them.
 */
function resolveStripingSurcharges(item: LineItem, pricingItem?: PricingItem): { newLayoutAdd: number; doubleCoatAdd: number } {
  return {
    newLayoutAdd: item.newLayoutAdditionalCost ?? pricingItem?.newLayoutAdditionalCost ?? 0,
    doubleCoatAdd: item.doubleCoatAdditionalCost ?? pricingItem?.doubleCoatAdditionalCost ?? 0,
  }
}

export function StripingSubSection({
  title, stripingCategory, lineItems, pricingItems, onAddItem, onUpdateItem, onDeleteItem,
}: StripingSubSectionProps) {
  // Filter pricing items to this striping sub-category
  const categoryItems = pricingItems.filter(
    (p) => p.category === 'striping' && p.stripingCategory === stripingCategory
  )

  // Filter line items to this sub-section
  const rows = lineItems.filter(
    (li) => li.category === 'striping' && li.stripingCategory === stripingCategory && li.section === 'striping'
  )

  // Build a map from pricingItemId to PricingItem for cost lookups
  const pricingMap = new Map<string, PricingItem>()
  categoryItems.forEach((p) => pricingMap.set(p.id, p))

  const handleSelect = async (item: PricingItem) => {
    await onAddItem({
      description: item.name,
      category: 'striping',
      quantity: 1,
      unit: item.unit || 'ea',
      rate: item.rate,
      pricingItemId: item.id,
      section: 'striping',
      stripingCategory: stripingCategory,
      newLayout: false,
      doubleCoat: false,
    })
  }

  const sectionTotal = rows.reduce((sum, li) => {
    const pi = li.pricingItemId ? pricingMap.get(li.pricingItemId) : undefined
    const { newLayoutAdd, doubleCoatAdd } = resolveStripingSurcharges(li, pi)
    return sum + calcStripingTotal(li.quantity, li.rate, li.newLayout || false, li.doubleCoat || false, newLayoutAdd, doubleCoatAdd)
  }, 0)

  return (
    <div className="sc-section">
      <div className="sc-section-header">
        <StripingIcon />
        <span className="sc-section-title">{title}</span>
        <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
          <ItemPickerDropdown
            items={categoryItems}
            onSelect={handleSelect}
            buttonLabel="Add Item"
          />
        </div>
      </div>

      <div className="sc-table-wrap">
        <table className="sc-table st-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>QTY</th>
              <th>Cost/Unit</th>
              <th>New Layout</th>
              <th>Double Coat</th>
              <th className="col-right">Total Cost</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((li) => (
              <StripingRow
                key={li.id}
                item={li}
                pricingItem={li.pricingItemId ? pricingMap.get(li.pricingItemId) : undefined}
                onUpdateItem={onUpdateItem}
                onDeleteItem={onDeleteItem}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} className="col-right"><strong>Total</strong></td>
                <td className="col-right"><span className="sc-foot-total">{fmt(sectionTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

/* ─── Row Component ─── */
interface StripingRowProps {
  item: LineItem
  pricingItem?: PricingItem
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

function StripingRow({ item, pricingItem, onUpdateItem, onDeleteItem }: StripingRowProps) {
  const [localDescription, setLocalDescription] = useState(item.description)
  const [localQty, setLocalQty] = useState(item.quantity)
  const [localQtyDisplay, setLocalQtyDisplay] = useState(item.quantity === 0 ? '' : String(item.quantity))
  const [localRate, setLocalRate] = useState(item.rate)
  const [localRateDisplay, setLocalRateDisplay] = useState(item.rate === 0 ? '' : item.rate.toFixed(2))
  const [localRateEditing, setLocalRateEditing] = useState(false)
  const [localNewLayout, setLocalNewLayout] = useState(item.newLayout || false)
  const [localDoubleCoat, setLocalDoubleCoat] = useState(item.doubleCoat || false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const focusedRef = useRef(false)

  // Persist the FULL field set (not just the field that changed) so the store never
  // holds a stale raw field beside a fresh lineTotal. `over` carries the value typed
  // this tick, before its setState has flushed; the rest come from the latest render.
  const { newLayoutAdd, doubleCoatAdd } = resolveStripingSurcharges(item, pricingItem)

  const commit = useCallback((over?: { qty?: number; rate?: number; nl?: boolean; dc?: boolean }) => {
    const q = over?.qty ?? localQty
    const r = over?.rate ?? localRate
    const nl = over?.nl ?? localNewLayout
    const dc = over?.dc ?? localDoubleCoat
    const lineTotal = calcStripingTotal(q, r, nl, dc, newLayoutAdd, doubleCoatAdd)
    onUpdateItem(item.id, { quantity: q, rate: r, newLayout: nl, doubleCoat: dc, lineTotal })
  }, [item.id, localQty, localRate, localNewLayout, localDoubleCoat, newLayoutAdd, doubleCoatAdd, onUpdateItem])

  const commitRef = useRef(commit)
  commitRef.current = commit

  const debouncedSave = useCallback((over?: { qty?: number; rate?: number; nl?: boolean; dc?: boolean }) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => { timerRef.current = null; commit(over) }, 600)
  }, [commit])

  const flushSave = useCallback((over?: { qty?: number; rate?: number; nl?: boolean; dc?: boolean }) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    commit(over)
  }, [commit])

  // Re-sync local fields from the store item when it changes externally, unless the
  // user is editing this row (so an in-progress edit is never stomped).
  useEffect(() => {
    if (focusedRef.current) return
    setLocalDescription(item.description)
    setLocalQty(item.quantity)
    setLocalQtyDisplay(item.quantity === 0 ? '' : String(item.quantity))
    setLocalRate(item.rate)
    setLocalRateDisplay(item.rate === 0 ? '' : item.rate.toFixed(2))
    setLocalNewLayout(item.newLayout || false)
    setLocalDoubleCoat(item.doubleCoat || false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  // Flush a pending edit on unmount (tab switch inside the debounce window).
  useEffect(() => () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; commitRef.current() }
  }, [])

  const total = calcStripingTotal(localQty, localRate, localNewLayout, localDoubleCoat, newLayoutAdd, doubleCoatAdd)

  return (
    <tr>
      <td className="col-name">
        <input
          type="text"
          className="sc-input"
          value={localDescription}
          onFocus={() => { focusedRef.current = true }}
          onChange={(e) => setLocalDescription(e.target.value)}
          onBlur={() => { focusedRef.current = false; onUpdateItem(item.id, { description: localDescription }) }}
        />
      </td>
      <td>
        <input
          type="text"
          className="sc-input"
          value={localQtyDisplay}
          onFocus={() => { focusedRef.current = true }}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === '' || /^(\d+\.?\d*|\.\d*)$/.test(raw)) {
              setLocalQtyDisplay(raw)
              const v = parseFloat(raw) || 0
              setLocalQty(v)
              debouncedSave({ qty: v })
            }
          }}
          onBlur={() => {
            const v = parseFloat(localQtyDisplay) || 0
            setLocalQty(v)
            setLocalQtyDisplay(v === 0 ? '' : String(v))
            focusedRef.current = false
            flushSave({ qty: v })
          }}
          onDoubleClick={selectAll}
        />
      </td>
      <td>
        <input
          type="text"
          className="sc-input"
          value={localRateEditing ? localRateDisplay : (localRate === 0 ? '$0.00' : '$' + localRate.toFixed(2))}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === '' || /^(\d+\.?\d*|\.\d*)$/.test(raw)) {
              setLocalRateDisplay(raw)
              const v = parseFloat(raw) || 0
              setLocalRate(v)
              debouncedSave({ rate: v })
            }
          }}
          onFocus={() => { setLocalRateEditing(true); focusedRef.current = true }}
          onBlur={() => {
            const v = parseFloat(localRateDisplay) || 0
            setLocalRate(v)
            setLocalRateDisplay(v === 0 ? '' : v.toFixed(2))
            setLocalRateEditing(false)
            focusedRef.current = false
            flushSave({ rate: v })
          }}
          onDoubleClick={selectAll}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        {pricingItem?.newLayoutEnabled !== false ? (
          <input
            type="checkbox"
            className="st-checkbox"
            checked={localNewLayout}
            onChange={(e) => {
              setLocalNewLayout(e.target.checked)
              flushSave({ nl: e.target.checked })
            }}
          />
        ) : (
          <span style={{ color: 'var(--s300)' }}>—</span>
        )}
      </td>
      <td style={{ textAlign: 'center' }}>
        {pricingItem?.doubleCoatEnabled !== false ? (
          <input
            type="checkbox"
            className="st-checkbox"
            checked={localDoubleCoat}
            onChange={(e) => {
              setLocalDoubleCoat(e.target.checked)
              flushSave({ dc: e.target.checked })
            }}
          />
        ) : (
          <span style={{ color: 'var(--s300)' }}>—</span>
        )}
      </td>
      <td className="col-right"><strong>{fmt(total)}</strong></td>
      <td className="col-action">
        <button onClick={() => onDeleteItem(item.id)} className="sc-icon-btn" title="Delete">
          <DeleteIcon />
        </button>
      </td>
    </tr>
  )
}
